/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useRef } from 'react'
import { FormikProps } from 'formik'
import { useDispatch } from 'react-redux'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Lightbox } from '@nexigendigital/nxui'
import { useUpdateSecurityMutation } from 'api/account'
import { ChangeSecurityQuestion } from 'models/account'
import { setUser } from 'store/slices/appSlice'
import { Timer } from 'helpers/utils'

import ChangeSecurityQuestionPasswordForm from 'containers/account/form/changeSecurityQuestionPasswordForm'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    status: boolean,
    close: () => void
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function ChangeSecurityQuestionLightbox({ status, close }: Props) {
    const dispatch = useDispatch()
    const formRef = useRef<FormikProps<ChangeSecurityQuestion>>(null)
    const [, { isLoading, isSuccess, isError, data, reset }] = useUpdateSecurityMutation({ fixedCacheKey: 'update-security' })

    /**********************************************************************************************************
     *   SIDE EFFECTS
     **********************************************************************************************************/
    useEffect(() => {
        if (isError && formRef.current) {
            formRef.current.setSubmitting(false)
            formRef.current.resetForm()
        }
        if (isSuccess && data) {
            dispatch(setUser(data.user))
        }
    }, [isSuccess, isError, dispatch, data])

    /*   RENDER COMPONENT
    **********************************************************************************************************/
    if (isSuccess) {
        return (
            <Lightbox
                title={'Details Updated'}
                type={'success'}
                icon={'success'}
                description={'You can now see this reflected on the Account page.'}
                conditions={{
                    state: status,
                    action: () => {
                        close()
                        Timer(() => {
                            reset()
                        }, 500)
                    }
                }}
                actions={[
                    {
                        label: 'Back to Account',
                        color: 'primary',
                        func: () => {
                            close()
                            Timer(() => {
                                reset()
                            }, 500)
                        }
                    }
                ]}
            />
        )
    }

    return (
        <Lightbox
            title={'Security Question'}
            content={<ChangeSecurityQuestionPasswordForm form={formRef}/>}
            conditions={{ state: status, action: () => close() }}
            links={[]}
            actions={[
                {
                    label: 'Cancel',
                    color: 'secondary',
                    func: () => close()
                },
                {
                    label: 'Update',
                    color: 'primary',
                    type: 'submit',
                    form: 'changeSecurityQuestionForm',
                    loading: isLoading ? 'Updating...' : false
                }
            ]}
        />
    )
}
